import SidebarConfig from '@customTypes/SidebarConfig';
import useSidebarConfig from '@hooks/useSidebarConfig';
import { IconifyIcon } from '@iconify/react';
import React, {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

export interface Config {
  label: string;
  labelClass?: string;
  icon?: string | IconifyIcon;
  path?: string;
  onClick?: (value: string) => void;
  children?: Config[];
  isAvailable?: boolean;
  hasNotification?: boolean;
}

interface SidebarContextType {
  selectedMenu: string;
  activeIndexMap: {
    [depth: number]: number[];
  };
  handleSetActiveIndexMap: (
    depth: number,
    index: number,
    isCollapsed: boolean,
  ) => void;
  hasNotification: boolean;
}

// ts-prune-ignore-next used in test
export const defaultContext = {
  selectedMenu: '',
  activeIndexMap: {},
  handleSetActiveIndexMap: () => {
    /* do nothing */
  },
  hasNotification: false,
};

// ts-prune-ignore-next used in test
export const SidebarContext = createContext<SidebarContextType>(defaultContext);

// list of depth that allowed to have only 1 active index
export const SINGLE_ACTIVE_DEPTH_LIST = [0];

const SidebarContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const [selectedMenu, setSelectedMenu] = useState('');
  const [activeIndexMap, setActiveIndexMap] = useState<{
    [depth: number]: number[];
  }>({});

  const handleSetActiveIndexMap = (
    depth: number,
    index: number,
    isCollapsed: boolean,
  ) => {
    setActiveIndexMap(prev => {
      let currentDepthValue = prev[depth] || [];

      if (SINGLE_ACTIVE_DEPTH_LIST.includes(depth)) {
        currentDepthValue = isCollapsed ? [index] : [];
      } else {
        currentDepthValue = isCollapsed
          ? [...currentDepthValue, index]
          : currentDepthValue.filter(val => val !== index);
      }

      return { ...prev, [depth]: currentDepthValue };
    });
  };
  const findSelectedMenu = useCallback(
    (cfgs: SidebarConfig[], parentIndexList: number[] = []) => {
      cfgs.forEach((cfg, index) => {
        if (cfg?.children?.length) {
          findSelectedMenu(cfg.children, [...parentIndexList, index]);
        } else {
          if (cfg.path !== pathname || !cfg.isAvailable) return;

          const activeIndexMap = parentIndexList.reduce<
            Record<number, number[]>
          >((acc, curr, index) => {
            acc[index] = [curr];
            return acc;
          }, {});

          setActiveIndexMap(activeIndexMap);
          setSelectedMenu(cfg.label);
        }
      });
    },
    [pathname],
  );

  const { configs: sidebarConfig, hasNotification } = useSidebarConfig();

  useEffect(() => {
    findSelectedMenu(sidebarConfig);
  }, [findSelectedMenu, sidebarConfig]);

  const sidebarContextValue = useMemo(
    () => ({
      selectedMenu,
      activeIndexMap,
      handleSetActiveIndexMap,
      hasNotification,
    }),
    [activeIndexMap, hasNotification, selectedMenu],
  );

  return (
    <SidebarContext.Provider value={sidebarContextValue}>
      {children}
    </SidebarContext.Provider>
  );
};

const useSidebarContext = () => {
  return useContext(SidebarContext);
};

export { SidebarContextProvider, useSidebarContext };
